import { useCallback } from 'react';
import { useAtom } from 'jotai';
import {
    paymentPopupOpenAtom,
    paymentPopupUrlSegmentAtom,
    paymentPopupStripeAccountAtom,
    paymentPopupPaymentIdAtom,
    paymentPopupPublishableKeyAtom,
} from '../states/payment-popup-atoms';

/**
 * Custom hook for managing payment popup state and logic
 * @returns Object containing popup state and control functions
 */
export const usePaymentPopup = () => {
    const [isPaymentPopupOpen, setIsPaymentPopupOpen] =
        useAtom(paymentPopupOpenAtom);
    const [currentUrlSegment, setCurrentUrlSegment] = useAtom(
        paymentPopupUrlSegmentAtom,
    );
    const [stripeAccount, setStripeAccount] = useAtom(
        paymentPopupStripeAccountAtom,
    );
    const [currentPaymentId, setCurrentPaymentId] = useAtom(
        paymentPopupPaymentIdAtom,
    );
    const [publishableKey, setPublishableKey] = useAtom(
        paymentPopupPublishableKeyAtom,
    );

    const openPopup = useCallback(
        (
            urlSegment: string,
            stripeAccountParam: string | null,
            paymentId?: number,
            publishableKeyParam?: string | null,
        ) => {
            setCurrentUrlSegment(urlSegment);
            setIsPaymentPopupOpen(true);
            setStripeAccount(stripeAccountParam);
            setCurrentPaymentId(paymentId || 0);
            setPublishableKey(publishableKeyParam || null);
        },
        [
            setCurrentUrlSegment,
            setIsPaymentPopupOpen,
            setStripeAccount,
            setCurrentPaymentId,
            setPublishableKey,
        ],
    );

    const closePopup = useCallback(() => {
        setIsPaymentPopupOpen(false);
        setCurrentUrlSegment('');
        setStripeAccount(null);
        setCurrentPaymentId(0);
        setPublishableKey(null);
    }, [
        setIsPaymentPopupOpen,
        setCurrentUrlSegment,
        setStripeAccount,
        setCurrentPaymentId,
        setPublishableKey,
    ]);

    return {
        isPaymentPopupOpen,
        openPopup,
        closePopup,
        currentUrlSegment,
        stripeAccount,
        currentPaymentId,
        publishableKey,
    };
};
